import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ErgonomicTableContainer from "../components/common/ErgonomicTableContainer";
import {
  ScrollText, Search, RotateCcw, ChevronLeft, ChevronRight, Bot, User as UserIcon, Download,
} from "lucide-react";
import { format, subDays, startOfMonth } from "date-fns";
import { toast } from "sonner";

const EMPTY_DATES = { from: "", to: "" };
const iso = (d) => format(d, "yyyy-MM-dd");
const DATE_PRESETS = [
  { v: "all", label: "Any date" },
  { v: "today", label: "Today" },
  { v: "7d", label: "Last 7 days" },
  { v: "month", label: "This month" },
  { v: "custom", label: "Custom range" },
];
function presetRange(v) {
  const now = new Date();
  if (v === "today") return { from: iso(now), to: iso(now) };
  if (v === "7d") return { from: iso(subDays(now, 6)), to: iso(now) };
  if (v === "month") return { from: iso(startOfMonth(now)), to: iso(now) };
  return EMPTY_DATES;
}

const BADGE_CLASS = {
  success: "bg-emerald-100 text-emerald-800",
  danger: "bg-red-100 text-red-800",
  info: "bg-blue-100 text-blue-800",
  neutral: "bg-slate-100 text-slate-600",
};

const fmtWhen = (iso_) => {
  if (!iso_) return "—";
  try { return new Date(iso_).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).replace(",", ","); } catch { return "—"; }
};
const roleLabel = (role) => (role ? role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");
const PAGE_SIZE = 50;

export default function AuditTrail() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ actors: [], actions: [] });

  const [search, setSearch] = useState("");
  const [dSearch, setDSearch] = useState("");
  const [actor, setActor] = useState("all");
  const [action, setAction] = useState("all");
  const [dates, setDates] = useState(EMPTY_DATES);
  const [preset, setPreset] = useState("all");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/admin/audit-logs/filter-options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setDSearch(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = {
    page, limit: PAGE_SIZE,
    search: dSearch || undefined,
    actor: actor !== "all" ? actor : undefined,
    action: action !== "all" ? action : undefined,
    from_date: dates.from || undefined,
    to_date: dates.to || undefined,
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/audit-logs", { params })
      .then((r) => { setRows(r.data.items || []); setTotal(r.data.total ?? 0); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dSearch, actor, action, dates.from, dates.to, page]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (v) => { setPreset(v); setPage(1); if (v !== "custom") setDates(presetRange(v)); };
  const setDate = (k) => (val) => { setPreset("custom"); setPage(1); setDates((d) => ({ ...d, [k]: val })); };

  const datesActive = Boolean(dates.from || dates.to);
  const activeCount = (dSearch ? 1 : 0) + (actor !== "all" ? 1 : 0) + (action !== "all" ? 1 : 0) + (datesActive ? 1 : 0);
  const clearFilters = () => {
    setSearch(""); setDSearch(""); setActor("all"); setAction("all"); setDates(EMPTY_DATES); setPreset("all"); setPage(1);
  };

  // Same filters as the table (minus page/limit — export isn't paginated,
  // it's capped server-side instead) so the download always matches
  // exactly what's currently on screen.
  const exportReport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/audit-logs/export", {
        params: {
          search: dSearch || undefined,
          actor: actor !== "all" ? actor : undefined,
          action: action !== "all" ? action : undefined,
          from_date: dates.from || undefined,
          to_date: dates.to || undefined,
          format: "xlsx",
        },
        responseType: "blob",
      });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : "audit_log.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const targetText = (t) => {
    if (!t) return "—";
    const parts = [];
    if (t.application_no) parts.push(t.application_no);
    if (t.name) parts.push(t.name);
    if (parts.length) return parts.join(" · ");
    if (t.application_id) return t.application_id.slice(0, 8) + "…";
    if (t.lead_id) return t.lead_id.slice(0, 8) + "…";
    return "—";
  };

  return (
    <div data-testid="audit-trail-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <ScrollText className="w-6 h-6" /> Institutional Audit &amp; Activity Log
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Tamper-evident record of stage transitions, verification decisions, offer letters, and administrative actions.
        </p>
      </div>

      <Card className="p-3 border-slate-200" data-testid="audit-trail-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="audit-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by applicant ID, name, or email..." className="pl-8 h-9 text-sm bg-white" />
          </div>

          <Select value={actor} onValueChange={(v) => { setActor(v); setPage(1); }}>
            <SelectTrigger data-testid="audit-filter-actor" className="w-[190px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              <SelectItem value="system">System Bot</SelectItem>
              {options.actors.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name} · {roleLabel(a.role)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
            <SelectTrigger data-testid="audit-filter-action" className="w-[210px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {options.actions.map((a) => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger data-testid="audit-filter-date-preset" className="w-[140px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1.5">
            <input type="date" data-testid="audit-from-date" value={dates.from} max={dates.to || undefined}
              onChange={(e) => setDate("from")(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700" aria-label="From date" />
            <span className="text-xs text-slate-400">–</span>
            <input type="date" data-testid="audit-to-date" value={dates.to} min={dates.from || undefined}
              onChange={(e) => setDate("to")(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700" aria-label="To date" />
          </div>

          {activeCount > 0 && (
            <Button variant="ghost" size="sm" data-testid="audit-clear-filters" onClick={clearFilters} className="text-slate-500">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Clear Filters ({activeCount})
            </Button>
          )}

          <span className="text-xs text-slate-500 sm:ml-auto" data-testid="audit-result-count">
            {total} event{total === 1 ? "" : "s"}
          </span>

          <Button variant="outline" size="sm" data-testid="audit-export-report"
            onClick={exportReport} disabled={exporting}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> {exporting ? "Exporting…" : "Export Report"}
          </Button>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="audit-trail-table-card">
        <ErgonomicTableContainer maxHeight="calc(100vh - 320px)" minWidth="1280px" testId="audit-trail-table-scroll">
          <table
            className="w-full text-sm min-w-[1280px] border-collapse
              [&_th]:px-3 [&_th]:h-11 [&_th]:whitespace-nowrap [&_th]:sticky [&_th]:top-0 [&_th]:z-20
              [&_th]:bg-slate-50 [&_th]:shadow-[inset_0_-1px_0_#e2e8f0] [&_th]:text-left
              [&_td]:px-3 [&_td]:py-2.5 [&_td]:align-top [&_tr]:border-b [&_tr]:border-slate-100"
          >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {[
                  ["Timestamp", "min-w-[170px]"], ["Actor", "min-w-[190px]"], ["Action", "min-w-[150px]"],
                  ["Target", "min-w-[200px]"], ["Details / Delta", "min-w-[260px]"], ["IP / Session", "min-w-[180px]"],
                ].map(([h, w]) => (
                  <TableHead key={h} className={`${w} text-xs font-semibold uppercase tracking-wider text-slate-500`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No audit events match these filters.</TableCell></TableRow>
              )}
              {!loading && rows.map((r, i) => (
                <TableRow key={r.id || i} data-testid={`audit-row-${i}`} className="hover:bg-slate-50/60">
                  <TableCell className="font-mono text-xs text-slate-600 whitespace-nowrap">{fmtWhen(r.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {r.actor?.is_system
                        ? <Bot className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                        : <UserIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{r.actor?.name || "Unknown"}</div>
                        <div className="text-[11px] text-slate-400">
                          {r.actor?.is_system ? "System · Ingestion Engine" : roleLabel(r.actor?.role)}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={BADGE_CLASS[r.badge] || BADGE_CLASS.neutral} data-testid={`audit-action-badge-${i}`}>
                      {r.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-700">{targetText(r.target)}</TableCell>
                  <TableCell className="text-slate-600">{r.details || "—"}</TableCell>
                  <TableCell>
                    {/* Defensive fallback chain: the API's own field is always
                        "ip" (routes_admin.list_audit_logs / audit.log_audit),
                        but this tolerates any other shape — a future action
                        type nesting it under meta.ip, or a renamed field —
                        without the column silently going blank again. */}
                    <div className="text-xs font-mono text-slate-600">
                      {r.ip || r.ip_address || r.client_ip || r.meta?.ip || "—"}
                    </div>
                    {(r.user_agent || r.meta?.user_agent) && (
                      <div className="text-[11px] text-slate-400 truncate max-w-[220px]"
                        title={r.user_agent || r.meta?.user_agent}>
                        {r.user_agent || r.meta?.user_agent}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </table>
        </ErgonomicTableContainer>
      </Card>

      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500" data-testid="audit-pagination-info">
          Page {page} of {totalPages} · {total} event{total === 1 ? "" : "s"}
        </span>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" data-testid="audit-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" data-testid="audit-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
